import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { TransactionType } from '../../utils/enums';
import { SUPPORTED_CURRENCIES } from '../../utils/currencies';
import TokenSelector from '../allocation/TokenSelector';
import SetStockModal from './SetStockModal';
import StockFxSnapshotFields from '../StockFxSnapshotFields';
import { stockFxSnapshotChanges, stockFxSnapshotState, updateStockFxSnapshot } from '../../utils/stockFxSnapshot';
import { normalizeDecimalInput, parseFloatFromString } from '../../utils/formatters';

const typeOptions = [
  { label: 'Buy', value: TransactionType.BUY },
  { label: 'Sell', value: TransactionType.SELL },
  { label: 'Transfer In', value: TransactionType.TRANSFER_IN },
  { label: 'Transfer Out', value: TransactionType.TRANSFER_OUT },
  { label: 'Internal Transfer', value: TransactionType.INTERNAL_TRANSFER },
];

const formatDateInput = (value) => {
  if (!value) return '';
  try {
    return format(new Date(value), "yyyy-MM-dd'T'HH:mm");
  } catch (_) {
    return '';
  }
};

const defaultState = {
  tokenId: '',
  coingeckoApiId: '',
  transactionType: TransactionType.BUY,
  price: '',
  quantity: '',
  timestamp: '',
  fees: '',
  feeQuantity: '',
  currency: 'USD',
  notes: '',
};

const allowedTransactionTypes = new Set([
  TransactionType.BUY,
  TransactionType.SELL,
  TransactionType.TRANSFER_IN,
  TransactionType.TRANSFER_OUT,
  TransactionType.INTERNAL_TRANSFER,
  TransactionType.SPLIT,
]);

const normalizeTransactionType = (value) => {
  if (!value) return TransactionType.BUY;
  const upper = value.toString().toUpperCase();
  return allowedTransactionTypes.has(upper) ? upper : TransactionType.BUY;
};

const EditPreviewModal = ({ isOpen, record, isStock = false, onClose, onSave, loading }) => {
  const availableTypes = isStock ? [...typeOptions, { label: 'Stock Split', value: TransactionType.SPLIT }] : typeOptions;
  const [stockSelection, setStockSelection] = useState(null);
  const [stockLabel, setStockLabel] = useState('');
  const [formData, setFormData] = useState(defaultState);
  const [errors, setErrors] = useState({});
  const [selectedTokenDetails, setSelectedTokenDetails] = useState(null);
  const [activeSelectorIndex, setActiveSelectorIndex] = useState(null);

  useEffect(() => {
    if (isOpen && record) {
      setStockSelection(null);
      setStockLabel(record.token?.ticker || record.stockId || 'Unresolved (you can still correct this row)');
      // Determine if there's a local token or just a coingecko API ID for deferred import
      const hasLocalToken = record.token?.id || record.tokenId;
      const initialTokenId = hasLocalToken ? (record.token?.id || record.tokenId) : '';
      const initialCoingeckoApiId = !hasLocalToken && record.rawCoingeckoId ? record.rawCoingeckoId : '';
      
      const initialTokenDetails =
        record.token && record.token.id
          ? {
              id: record.token.id,
              name: record.token.name || 'Unknown Token',
              ticker: record.token.ticker || 'N/A',
              coingeckoApiId: record.token.coingeckoApiId || null,
              tokenId: record.token.id,
              isLocalToken: true,
            }
          : initialCoingeckoApiId
            ? {
                id: initialCoingeckoApiId,
                name: record.resolvedTokenName || initialCoingeckoApiId,
                ticker: record.rawTicker || initialCoingeckoApiId,
                coingeckoApiId: initialCoingeckoApiId,
                isLocalToken: false,
              }
            : initialTokenId
              ? {
                  id: initialTokenId,
                  name: record.token?.name || initialTokenId,
                  ticker: record.token?.ticker || initialTokenId,
                  tokenId: initialTokenId,
                  isLocalToken: true,
                }
              : null;

      setFormData({
        ...stockFxSnapshotState(record),
        tokenId: initialTokenId,
        coingeckoApiId: initialCoingeckoApiId,
        transactionType: normalizeTransactionType(record.transactionType),
        price: record.price ?? '',
        quantity: record.quantity ?? '',
        timestamp: formatDateInput(record.timestamp),
        fees: record.fees ?? '',
        feeQuantity: record.feeQuantity ?? '',
        currency: record.currency || (isStock ? '' : 'USD'),
        notes: record.notes || '',
      });
      setSelectedTokenDetails(initialTokenDetails);
      setErrors({});
      setActiveSelectorIndex(null);
    }
  }, [isOpen, record, isStock]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || activeSelectorIndex === 'stock') {
        return;
      }
      event.preventDefault();
      if (activeSelectorIndex !== null) {
        setActiveSelectorIndex(null);
      } else {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, activeSelectorIndex, onClose]);

  const modalTitle = useMemo(() => {
    if (!record) return 'Edit Preview Transaction';
    return `Edit Preview Transaction #${record.id.slice(0, 6)}`;
  }, [record]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => isStock ? updateStockFxSnapshot(prev, name, value) : { ...prev, [name]: value });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleTokenSelected = (token) => {
    // Determine if this is a local token (has UUID tokenId) or a CoinGecko search result
    const isLocalToken = token?.tokenId && token.tokenId.includes('-'); // UUIDs have dashes
    const nextTokenId = isLocalToken ? token.tokenId : '';
    const nextCoingeckoApiId = !isLocalToken && token?.coingeckoApiId ? token.coingeckoApiId : '';
    
    const nextTokenDetails = token
      ? {
          ...token,
          id: token.id || token.tokenId || token.coingeckoApiId || '',
          isLocalToken,
        }
      : null;

    setFormData((prev) => ({
      ...prev,
      tokenId: nextTokenId,
      coingeckoApiId: nextCoingeckoApiId,
    }));
    setSelectedTokenDetails(nextTokenDetails);
    setErrors((prev) => (prev.tokenId ? { ...prev, tokenId: null } : prev));
    setActiveSelectorIndex(null);
  };

  const selectorIndex = 0;
  const isSelectorActive = activeSelectorIndex === selectorIndex;
  const toggleSelector = (index) => {
    setActiveSelectorIndex((prev) => (prev === index ? null : index));
  };

  const handleTypeSelect = (value) => {
    setFormData((prev) => ({
      ...prev,
      transactionType: value,
      ...(value === TransactionType.SPLIT ? { price: '0', fees: '0', feeQuantity: '0' } : {}),
    }));
    if (TransactionType.isTransfer(value)) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.price;
        return next;
      });
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (formData.transactionType === TransactionType.SPLIT) {
      if (!isStock) nextErrors.transactionType = 'Stock splits are only supported for stocks';
      const ratio = parseFloatFromString(formData.quantity);
      if (!Number.isFinite(ratio) || !(ratio > 0) || ratio === 1) nextErrors.quantity = 'Split ratio must be positive and differ from 1';
      if (parseFloatFromString(formData.price) !== 0) nextErrors.price = 'Split price must be zero';
      if (parseFloatFromString(formData.fees) !== 0) nextErrors.fees = 'Split fees must be zero';
    }
    if (!isStock && !formData.tokenId && !formData.coingeckoApiId) {
      nextErrors.tokenId = 'Token is required';
    }
    if (!formData.transactionType) {
      nextErrors.transactionType = 'Transaction type is required';
    }
    if (isStock || !TransactionType.isTransfer(formData.transactionType)) {
      if (!formData.price) {
        nextErrors.price = 'Price is required';
      } else if (Number.isNaN(parseFloatFromString(formData.price))) {
        nextErrors.price = 'Enter a valid number';
      }
    }
    if (!formData.quantity) {
      nextErrors.quantity = 'Quantity is required';
    } else if (Number.isNaN(parseFloatFromString(formData.quantity))) {
      nextErrors.quantity = 'Enter a valid number';
    }
    if (!formData.timestamp) {
      nextErrors.timestamp = 'Timestamp is required';
    }
    if (!formData.fees) {
      nextErrors.fees = 'Fees are required';
    } else if (Number.isNaN(parseFloatFromString(formData.fees))) {
      nextErrors.fees = 'Enter a valid number';
    }
    if (formData.feeQuantity) {
      if (Number.isNaN(parseFloatFromString(formData.feeQuantity))) {
        nextErrors.feeQuantity = 'Enter a valid number';
      } else if (formData.transactionType !== TransactionType.INTERNAL_TRANSFER && parseFloatFromString(formData.feeQuantity) !== 0) {
        nextErrors.feeQuantity = 'Token fee is only valid for internal transfers';
      }
    }
    if (!formData.currency) {
      nextErrors.currency = 'Currency is required';
    } else if (isStock && !/^[A-Za-z]{3}$/.test(formData.currency.trim())) {
      nextErrors.currency = 'Enter a three-letter trading currency code';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!record || !validate()) {
      return;
    }

    const payload = {
      // Send tokenId for local tokens, coingeckoApiId for CoinGecko search results
      ...(isStock ? (stockSelection || {}) : {
        tokenId: formData.tokenId || null,
        coingeckoApiId: formData.coingeckoApiId || null,
      }),
      ...(isStock ? stockFxSnapshotChanges(formData, record) : {}),
      transactionType: formData.transactionType,
      price: !isStock && TransactionType.isTransfer(formData.transactionType)
        ? '0'
        : normalizeDecimalInput(formData.price),
      quantity: normalizeDecimalInput(formData.quantity),
      timestamp: formData.timestamp === formatDateInput(record.timestamp)
        ? record.timestamp
        : new Date(formData.timestamp).toISOString(),
      fees: formData.fees ? normalizeDecimalInput(formData.fees) : '0',
      feeQuantity: formData.transactionType === TransactionType.INTERNAL_TRANSFER && formData.feeQuantity
        ? normalizeDecimalInput(formData.feeQuantity)
        : '0',
      notes: formData.notes || null,
      currency: formData.currency || 'USD',
    };

    await onSave(payload);
  };

  if (!isOpen || !record) {
    return null;
  }

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{modalTitle}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
          >
            <span className="sr-only">Close</span>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {isStock && (
            <div className="space-y-2 text-sm">
              <StockFxSnapshotFields
                values={formData}
                original={record}
                onChange={(name, value) => handleChange({ target: { name, value } })}
                disabled={loading}
              />
              <p className="text-amber-700 dark:text-amber-300">Changing a historical stock does not automatically adjust quantities or prices for splits or mergers. Check the historical values and currency before importing.</p>
              <p className="text-gray-600 dark:text-gray-300">Original instrument: {[record.rawIsin, record.rawExchange].filter(Boolean).join(' · ') || 'Not provided'}</p>
              <p className="text-gray-900 dark:text-gray-100">Selected stock: {stockLabel}</p>
              <button type="button" onClick={() => setActiveSelectorIndex('stock')} className="text-blue-600 dark:text-blue-400 underline">Search / change stock</button>
            </div>
          )}
          {!isStock && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Token</label>
            <TokenSelector
              selectedTokenId={formData.tokenId || ''}
              selectedToken={selectedTokenDetails}
              selectedCoingeckoApiId={selectedTokenDetails?.coingeckoApiId || null}
              onSelectToken={handleTokenSelected}
              index={selectorIndex}
              isActive={isSelectorActive}
              toggleActive={toggleSelector}
              existingTokenIds={[]}
              existingCoingeckoApiIds={[]}
            />
            {errors.tokenId && <p className="text-xs text-red-500 mt-1">{errors.tokenId}</p>}
          </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Transaction Type</label>
            <div className="inline-flex max-w-full overflow-x-auto rounded-md border border-gray-300 dark:border-gray-600">
              {availableTypes.map((option, index) => {
                const isActive = formData.transactionType === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleTypeSelect(option.value)}
                    className={`px-3 py-1.5 text-xs sm:text-sm whitespace-nowrap transition-colors ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'
                    } ${index !== 0 ? 'border-l border-gray-300 dark:border-gray-600' : ''}`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            {errors.transactionType && <p className="text-xs text-red-500 mt-1">{errors.transactionType}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{formData.transactionType === TransactionType.SPLIT ? 'Split ratio (new shares per old share)' : 'Quantity'}</label>
              {formData.transactionType === TransactionType.SPLIT && <p className="mb-1 text-xs text-gray-500">4 = 4-for-1; 0.1 = 1-for-10. No cash or P&amp;L.</p>}
              <input
                type="text"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                className={`w-full rounded-md border px-3 py-2 text-sm text-gray-900 dark:text-gray-100 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.quantity ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="0.00"
              />
              {errors.quantity && <p className="text-xs text-red-500 mt-1">{errors.quantity}</p>}
            </div>
            {(isStock || !TransactionType.isTransfer(formData.transactionType)) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Price</label>
                <input
                  type="text"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  className={`w-full rounded-md border px-3 py-2 text-sm text-gray-900 dark:text-gray-100 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.price ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="0.00"
                />
                {errors.price && <p className="text-xs text-red-500 mt-1">{errors.price}</p>}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Fees</label>
              <input
                type="text"
                name="fees"
                value={formData.fees}
                onChange={handleChange}
                className={`w-full rounded-md border px-3 py-2 text-sm text-gray-900 dark:text-gray-100 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.fees ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="0.00"
              />
              {errors.fees && <p className="text-xs text-red-500 mt-1">{errors.fees}</p>}
            </div>
            {formData.transactionType === TransactionType.INTERNAL_TRANSFER && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{isStock ? 'Share fee' : 'Token fee'}</label>
                <input
                  type="text"
                  name="feeQuantity"
                  value={formData.feeQuantity}
                  onChange={handleChange}
                  className={`w-full rounded-md border px-3 py-2 text-sm text-gray-900 dark:text-gray-100 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.feeQuantity ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="0"
                />
                {errors.feeQuantity && <p className="text-xs text-red-500 mt-1">{errors.feeQuantity}</p>}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Currency</label>
              {isStock ? (
                <input
                  type="text"
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  placeholder="Trading currency, e.g. PLN"
                  className={`w-full rounded-md border px-3 py-2 text-sm text-gray-900 dark:text-gray-100 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.currency ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
              ) : (
              <select
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                className={`w-full rounded-md border px-3 py-2 text-sm text-gray-900 dark:text-gray-100 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.currency ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                {!SUPPORTED_CURRENCIES.includes(formData.currency) && (
                  <option value={formData.currency}>{formData.currency || 'Select currency'}</option>
                )}
                {SUPPORTED_CURRENCIES.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
              )}
              {errors.currency && <p className="text-xs text-red-500 mt-1">{errors.currency}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Timestamp</label>
            <input
              type="datetime-local"
              name="timestamp"
              value={formData.timestamp}
              onChange={handleChange}
              className={`w-full rounded-md border px-3 py-2 text-sm text-gray-900 dark:text-gray-100 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.timestamp ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            {errors.timestamp && <p className="text-xs text-red-500 mt-1">{errors.timestamp}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Optional notes"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 rounded-md border border-transparent text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
    <SetStockModal
      isOpen={activeSelectorIndex === 'stock'}
      selectedCount={1}
      initialQuery={record.token?.ticker || record.rawIsin || ''}
      hint="Selection changes only the instrument, not the price, quantity or currency."
      onClose={() => setActiveSelectorIndex(null)}
      onSave={(selection, stock) => {
        setStockSelection(selection);
        setStockLabel([stock.symbol, stock.name, stock.exchange].filter(Boolean).join(' · '));
        setActiveSelectorIndex(null);
      }}
      loading={loading}
    />
    </>
  );
};

export default EditPreviewModal;

